import { api } from './axios';

/**
 * Work API — клиент для управления работами участников в баттлах и дуэлях
 * 
 * Work — это агрегирующая сущность, которая связывает текстовые и аудио работы
 * с системой оценок.
 * 
 * @see work_api.md
 */

// ==================== Создание и получение работ ====================

/**
 * Создать работу
 * @param {Object} data - Данные работы
 * @param {string} [data.battlePersonId] - ID участника баттла
 * @param {string} [data.roundId] - ID раунда баттла
 * @param {string} [data.duelParticipantId] - ID участника дуэли
 * @param {string} [data.duelRoundId] - ID раунда дуэли
 * @param {string} [data.textId] - ID текстовой работы
 * @param {string} [data.trackId] - ID аудио трека
 * @param {string} [data.status] - Статус работы: SUBMITTED, APPROVED, DISQUALIFIED, DRAFT
 * @returns {Promise<Object>} Созданная работа
 */
export const createWork = (data) => {
    return api.post('/works', data).then((res) => res.data);
};

/**
 * Получить работу по ID
 * @param {string} id - ID работы
 * @returns {Promise<Object>} Детали работы
 */
export const getWorkById = (id) => {
    return api.get(`/works/${id}`).then((res) => res.data);
};

/**
 * Обновить работу
 * @param {string} id - ID работы
 * @param {Object} data - Данные для обновления
 * @param {string} [data.textId] - ID текстовой работы
 * @param {string} [data.trackId] - ID аудио трека
 * @param {string} [data.status] - Статус работы
 * @returns {Promise<Object>} Обновлённая работа
 */
export const updateWork = (id, data) => {
    return api.put(`/works/${id}`, data).then((res) => res.data);
};

/**
 * Удалить работу
 * @param {string} id - ID работы
 * @returns {Promise<void>}
 */
export const deleteWork = (id) => {
    return api.delete(`/works/${id}`).then((res) => res.data);
};

// ==================== Работы в раунде баттла ====================

/**
 * Получить работы в раунде баттла (с пагинацией)
 * @param {string} roundId - ID раунда баттла
 * @param {Object} [params] - Параметры пагинации
 * @param {number} [params.page=0] - Номер страницы
 * @param {number} [params.size=20] - Размер страницы (макс. 100)
 * @param {string} [params.sortBy=createdAt] - Поле для сортировки
 * @param {string} [params.direction=ASC] - Направление сортировки
 * @returns {Promise<Object>} Пагинированный список работ
 */
export const getWorksByRound = (roundId, params = {}) => {
    return api.get(`/works/round/${roundId}`, { params }).then((res) => res.data);
};

/**
 * Получить работу участника в раунде баттла
 * @param {string} roundId - ID раунда баттла
 * @param {string} personId - ID участника баттла
 * @returns {Promise<Object>} Работа участника
 */
export const getWorkByRoundAndPerson = (roundId, personId) => {
    return api.get(`/works/round/${roundId}/person/${personId}`).then((res) => res.data);
};

/**
 * Проверить наличие работы у участника в раунде баттла
 * @param {string} roundId - ID раунда баттла
 * @param {string} personId - ID участника баттла
 * @returns {Promise<{exists: boolean}>}
 */
export const checkWorkExists = (roundId, personId) => {
    return api.get(`/works/round/${roundId}/person/${personId}/exists`).then((res) => res.data);
};

/**
 * Получить работы участника в баттле
 * @param {string} battlePersonId - ID участника баттла
 * @returns {Promise<Array>} Список работ участника
 */
export const getWorksByBattlePerson = (battlePersonId) => {
    return api.get(`/works/person/battle/${battlePersonId}`).then((res) => res.data);
};

// ==================== Работы в раунде дуэли ====================

/**
 * Получить работы в раунде дуэли (с пагинацией)
 * @param {string} duelRoundId - ID раунда дуэли
 * @param {Object} [params] - Параметры пагинации
 * @param {number} [params.page=0] - Номер страницы
 * @param {number} [params.size=20] - Размер страницы (макс. 100)
 * @param {string} [params.sortBy=createdAt] - Поле для сортировки
 * @param {string} [params.direction=ASC] - Направление сортировки
 * @returns {Promise<Object>} Пагинированный список работ
 */
export const getWorksByDuelRound = (duelRoundId, params = {}) => {
    return api.get(`/works/duel-round/${duelRoundId}`, { params }).then((res) => res.data);
};

/**
 * Получить работу участника в раунде дуэли
 * @param {string} duelRoundId - ID раунда дуэли
 * @param {string} personId - ID участника дуэли
 * @returns {Promise<Object>} Работа участника
 */
export const getWorkByDuelRoundAndPerson = (duelRoundId, personId) => {
    return api.get(`/works/duel-round/${duelRoundId}/person/${personId}`).then((res) => res.data);
};

/**
 * Проверить наличие работы у участника в раунде дуэли
 * @param {string} duelRoundId - ID раунда дуэли
 * @param {string} personId - ID участника дуэли
 * @returns {Promise<{exists: boolean}>}
 */
export const checkDuelWorkExists = (duelRoundId, personId) => {
    return api.get(`/works/duel-round/${duelRoundId}/person/${personId}/exists`).then((res) => res.data);
};

/**
 * Получить работы участника в дуэли
 * @param {string} duelParticipantId - ID участника дуэли
 * @returns {Promise<Array>} Список работ участника
 */
export const getWorksByDuelParticipant = (duelParticipantId) => {
    return api.get(`/works/person/duel/${duelParticipantId}`).then((res) => res.data);
};

// ==================== Модерация работ ====================

/**
 * Одобрить работу (ADMIN/MODERATOR)
 * @param {string} id - ID работы
 * @returns {Promise<Object>} Обновлённая работа
 */
export const approveWork = (id) => {
    return api.post(`/works/${id}/approve`).then((res) => res.data);
};

/**
 * Дисквалифицировать работу (ADMIN/MODERATOR)
 * @param {string} id - ID работы
 * @param {string} reason - Причина дисквалификации (макс. 500 символов)
 * @returns {Promise<Object>} Обновлённая работа
 */
export const disqualifyWork = (id, reason) => {
    return api.post(`/works/${id}/disqualify`, { reason }).then((res) => res.data);
};

// ==================== Статистика ====================

/**
 * Получить статистику работ в раунде баттла
 * @param {string} roundId - ID раунда баттла
 * @returns {Promise<Object>} Статистика работ
 */
export const getRoundWorkStats = (roundId) => {
    return api.get(`/works/round/${roundId}/stats`).then((res) => res.data);
};

/**
 * Получить статистику работ в раунде дуэли
 * @param {string} duelRoundId - ID раунда дуэли
 * @returns {Promise<Object>} Статистика работ
 */
export const getDuelRoundWorkStats = (duelRoundId) => {
    return api.get(`/works/duel-round/${duelRoundId}/stats`).then((res) => res.data);
};

export default {
    createWork,
    getWorkById,
    updateWork,
    deleteWork,
    getWorksByRound,
    getWorkByRoundAndPerson,
    checkWorkExists,
    getWorksByBattlePerson,
    getWorksByDuelRound,
    getWorkByDuelRoundAndPerson,
    checkDuelWorkExists,
    getWorksByDuelParticipant,
    approveWork,
    disqualifyWork,
    getRoundWorkStats,
    getDuelRoundWorkStats
};
